import React, { createContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

export const GlobalContext = createContext();

export const GlobalProvider = ({ children }) => {
  // ========= ENV =========
  const baseUrl = process.env.REACT_APP_SALES_API_BASE_URL;
  const username = process.env.REACT_APP_API_AUTH_USERNAME;
  const password = process.env.REACT_APP_API_AUTH_PASSWORD;
  const appName = process.env.REACT_APP_APP_NAME || "WebApp";

  const basicAuth = useMemo(() => {
    return "Basic " + btoa(`${username}:${password}`);
  }, [username, password]);

  // ========= USER FROM SESSION =========
  const getStoredUser = () => {
    try {
      const storedUser = sessionStorage.getItem("user");

      if (!storedUser) return null;

      return JSON.parse(storedUser);
    } catch (error) {
      console.error("Invalid session user:", error);
      return null;
    }
  };

  // ========= GLOBAL STATES =========

const [latitude, setLatitude] = useState(null);
const [longitude, setLongitude] = useState(null);
const [locationLoading, setLocationLoading] = useState(true);
const [locationError, setLocationError] = useState("");
  const [user, setUser] = useState(getStoredUser);

  // ========= USER HELPERS =========
  const saveUser = (userData) => {
    if (!userData) return;

    sessionStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
  };

  const clearUser = () => {
    sessionStorage.removeItem("user");
    setUser(null);
  };

  const userCode =
    user?.userCode ||
    user?.usercode ||
    user?.userid ||
    user?.userId ||
    user?.code ||
    "";

  // ========= AUTH HEADERS =========
  const authHeaders = useMemo(() => {
    return {
      Authorization: basicAuth,
      "Content-Type": "application/json",
      "X-App-Name": appName,
    };
  }, [basicAuth, appName]);

  // ========= AXIOS INSTANCE =========
  const api = useMemo(() => {
    return axios.create({
      baseURL: baseUrl,
      headers: authHeaders,
    });
  }, [baseUrl, authHeaders]);

  // ========= GEOLOCATION =========
useEffect(() => {
  if (!navigator.geolocation) {
    setLocationError("Geolocation is not supported by this browser");
    setLocationLoading(false);
    return;
  }

  navigator.geolocation.getCurrentPosition(
    (position) => {
      setLatitude(position.coords.latitude);
      setLongitude(position.coords.longitude);
      setLocationError("");
      setLocationLoading(false);
    },
    (err) => {
      console.error("Geolocation error:", err.message);
      setLocationError(err.message);
      setLocationLoading(false);
    },
    {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 0,
    }
  );
}, []);

  // ========= CONTEXT VALUE =========
  const contextValue = useMemo(() => {
    return {
      baseUrl,
      appName,
      basicAuth,
      api,
      authHeaders,

      latitude,
      longitude,

      user,
      userCode,
      saveUser,
      clearUser,
    };
  }, [
    baseUrl,
    appName,
    basicAuth,
    api,
    authHeaders,
    latitude,
    longitude,
    locationLoading,
    locationError,
    user,
    userCode,
  ]);

  return (
    <GlobalContext.Provider value={contextValue}>
      {children}
    </GlobalContext.Provider>
  );
};